import { memo } from "react";
import { Check, ExternalLink, Heart } from "lucide-react";
import { usePrivacyMask } from "@/hooks/usePrivacyMask";
import { cn } from "@/utils";
import type { TemplatePickerTemplate } from "./templatePickerTypes";

type TemplateCardProps = {
  template: TemplatePickerTemplate;
  isSelected: boolean;
  isFavorite: boolean;
  isFavoritePending?: boolean;
  // Recebem o template/nome como argumento (em vez de closures pré-aplicadas)
  // para que TemplatePicker possa passar a MESMA referência de função a
  // todos os cards — memo só evita re-render de cards não afetados se as
  // props (incluindo essas) forem estáveis entre renders.
  onSelect: (template: TemplatePickerTemplate) => void;
  onToggleFavorite: (templateName: string) => void;
};

export const TemplateCard = memo(function TemplateCard({
  template,
  isSelected,
  isFavorite,
  isFavoritePending = false,
  onSelect,
  onToggleFavorite,
}: TemplateCardProps) {
  // Template names and texts are written per store and often carry its name:
  // name masked, texts blurred while the privacy mask is on.
  const { mask, blurClass } = usePrivacyMask();
  const now = new Date();
  const time = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
  const status = template.status ?? "APPROVED";

  const statusColor =
    {
      APPROVED: "text-[#0c7e30]",
      PENDING: "text-[#d37f00]",
      REJECTED: "text-[#8c0000]",
    }[status] ?? "text-gray-500";

  return (
    // div (não button) — o coração dentro é um <button>; <button> aninhado em
    // <button> é HTML inválido e quebra a navegação por teclado/leitor de tela.
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(template)}
      onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(template);
        }
      }}
      className={cn(
        "relative flex h-full cursor-pointer flex-col overflow-hidden rounded-xl text-left transition-all",
        isSelected
          ? "ring-2 ring-primary-v2-500 ring-offset-2"
          : "hover:ring-2 hover:ring-gray-300 hover:ring-offset-2"
      )}
    >
      <button
        type="button"
        disabled={isFavoritePending}
        onClick={e => {
          e.stopPropagation();
          onToggleFavorite(template.name);
        }}
        onKeyDown={e => {
          // Sem isso, Enter/Espaço com foco no coração sobe pro onKeyDown do
          // card (que faz preventDefault + onSelect) em vez de ativar o botão.
          e.stopPropagation();
        }}
        aria-label={isFavorite ? "Remover dos favoritos" : "Adicionar aos favoritos"}
        aria-pressed={isFavorite}
        className="absolute right-2 top-2 z-10 cursor-pointer rounded-full bg-white/90 p-1.5 shadow-sm transition-transform hover:scale-110 disabled:cursor-wait disabled:opacity-60"
      >
        <Heart
          size={14}
          className={isFavorite ? "fill-red text-red" : "text-gray-400"}
        />
      </button>

      <div className="flex-1 bg-[#e5ddd5] p-3">
        <div className="rounded-lg bg-white p-3 shadow-sm">
          {template.header && (
            <p className={cn("mb-2 text-sm font-bold text-gray-900", blurClass)}>{template.header}</p>
          )}
          <p className={cn("whitespace-pre-line text-sm text-gray-800", blurClass)}>
            {template.body.split(/(\{\{[^}]+\}\})/).map((part, i) =>
              part.match(/\{\{[^}]+\}\}/) ? (
                <span key={i} className="font-medium text-[#5c8a47]">
                  {part}
                </span>
              ) : (
                <span key={i}>{part}</span>
              )
            )}
          </p>
          {template.footer && (
            <p className={cn("mt-1 text-xs text-gray-500", blurClass)}>{template.footer}</p>
          )}
          <div className="mt-1 flex justify-end">
            <span className="text-[10px] text-gray-400">{time}</span>
          </div>
          {template.buttons?.map(btn => (
            <div key={btn.text} className="mt-2 border-t border-gray-200 pt-2">
              <div className="flex items-center justify-center gap-1 text-sm text-[#5c8a47]">
                <ExternalLink size={13} />
                {btn.text}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex shrink-0 items-center justify-between border-t border-gray-200 bg-gray-100 px-3 py-2">
        <span className="truncate text-xs text-gray-600">{mask(template.name)}</span>
        <div className="flex shrink-0 flex-col items-end">
          <span className={cn("text-xs font-medium", statusColor)}>{status}</span>
          <span className="text-xs text-gray-500">{template.category}</span>
        </div>
      </div>

      {isSelected && (
        <div className="absolute left-2 top-2 rounded-full bg-primary-v2-500 p-1 text-white">
          <Check size={13} />
        </div>
      )}
    </div>
  );
});
